import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AuthService, AuthState, GetTokenSilentlyOptions, IdToken, LogoutOptions, User } from '@auth0/auth0-angular';
import type { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { Observable, combineLatest, defer, map, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { EmbeddedSessionService, decodeIdTokenClaims } from './embedded-session.service';

/**
 * DI bridge for the embedded ROPG login (login redesign).
 *
 * ~130 files across the portals inject `AuthService` for `isAuthenticated$`,
 * `user$`, `logout()`… An embedded (ROPG) session lives outside the
 * auth0-spa-js client, so none of those consumers would see it. Rather than
 * touching every consumer, the three portal `app.config.ts` files provide THIS
 * class as `AuthService`: each overridden member merges the embedded session
 * in and falls back to the real SDK behaviour when no embedded session exists.
 * With the `embeddedLoginEnabled` flag off no embedded session can ever be
 * created, so every member behaves exactly like stock `AuthService`.
 *
 * `AuthGuard` (and `authGuardFn`) injects `AuthService`, so route protection
 * accepts the embedded session through this same bridge with no route edits.
 *
 * The base observables are plain aliases of `AuthState`'s streams (see the SDK
 * source), so the overrides rebuild from `AuthState` rather than referencing
 * their own base values (which TS forbids in a field initializer).
 */
@Injectable()
export class EmbeddedBridgedAuthService extends AuthService {
  private readonly embeddedSession = inject(EmbeddedSessionService);
  private readonly sdkState = inject(AuthState);
  // Repo convention for testable redirects (see calendar-button et al.):
  // navigate through the injected DOCUMENT, never bare window.location.
  private readonly documentRef = inject(DOCUMENT);

  override readonly isAuthenticated$: Observable<boolean> = combineLatest([
    this.sdkState.isAuthenticated$,
    this.embeddedSession.isActive$,
  ]).pipe(map(([sdkAuthenticated, embeddedActive]) => sdkAuthenticated || embeddedActive));

  override readonly user$: Observable<User | null | undefined> = combineLatest([
    this.sdkState.user$,
    this.embeddedSession.user$,
  ]).pipe(map(([sdkUser, embeddedUser]) => embeddedUser ?? sdkUser));

  override readonly idTokenClaims$: Observable<IdToken | null | undefined> = combineLatest([
    this.sdkState.idTokenClaims$,
    this.embeddedSession.session$,
  ]).pipe(
    map(([sdkClaims, session]) =>
      session && this.embeddedSession.hasValidSession()
        ? ({ ...decodeIdTokenClaims(session.idToken), __raw: session.idToken } as IdToken)
        : sdkClaims,
    ),
  );

  override getAccessTokenSilently(
    options: GetTokenSilentlyOptions & { detailedResponse: true },
  ): Observable<GetTokenSilentlyVerboseResponse>;
  override getAccessTokenSilently(options?: GetTokenSilentlyOptions): Observable<string>;
  override getAccessTokenSilently(
    options?: GetTokenSilentlyOptions & { detailedResponse?: true },
  ): Observable<string | GetTokenSilentlyVerboseResponse> {
    return defer(() => {
      const shape = (token: string): string | GetTokenSilentlyVerboseResponse =>
        options?.detailedResponse
          ? ({
              access_token: token,
              id_token: '',
              expires_in: 0,
              scope: '',
            } as unknown as GetTokenSilentlyVerboseResponse)
          : token;

      const sdkFallback = () =>
        options?.detailedResponse
          ? super.getAccessTokenSilently({ ...options, detailedResponse: true })
          : super.getAccessTokenSilently(options);

      const token = this.embeddedSession.accessToken;
      if (token) {
        return of(shape(token));
      }
      // Expired embedded session with a refresh token: silently refresh —
      // the "Silently" in this method's contract (mirrors the SDK behaviour).
      if (this.embeddedSession.canRefresh) {
        return this.embeddedSession.refresh().pipe(
          switchMap(session => of(shape(session.accessToken))),
          catchError(() => sdkFallback()),
        );
      }
      return sdkFallback();
    });
  }

  override logout(options?: LogoutOptions): Observable<void> {
    if (this.embeddedSession.hasValidSession()) {
      this.embeddedSession.clear();
      const returnTo = options?.logoutParams?.returnTo ?? this.documentRef.location.origin;
      this.documentRef.location.assign(returnTo);
      return of(undefined);
    }
    return super.logout(options);
  }
}
